/**
 * Canonical storefront-widget design tokens (ADR-028 Slice 1).
 *
 * Every leaf follows the DTCG token shape (`$type` + `$value`).  Components do
 * not import literal values from this file: `css.ts` emits the private
 * `--fmp-*` implementation properties and `theme.ts` validates the deliberately
 * smaller public `--fmp-theme-*` contract.
 */

export type DtcgTokenType =
  | 'color'
  | 'dimension'
  | 'duration'
  | 'fontFamily'
  | 'fontWeight'
  | 'number'
  | 'shadow'
  | 'strokeStyle'
  | 'cubicBezier';

/** Reverse-domain extension used only where CSS has no DTCG 2025.10 equivalent. */
export const FMP_CSS_TOKEN_EXTENSION = 'com.appofalltrades.find-my-part.css' as const;

export interface DtcgColorValue {
  readonly colorSpace: 'srgb';
  readonly components: readonly [number, number, number];
  readonly alpha?: number;
  readonly hex?: `#${string}`;
}

export interface DtcgDimensionValue {
  readonly value: number;
  readonly unit: 'px' | 'rem';
}

export interface DtcgDurationValue {
  readonly value: number;
  readonly unit: 'ms' | 's';
}

export interface DtcgToken<T, TType extends DtcgTokenType> {
  readonly $type: TType;
  readonly $value: T;
  readonly $description?: string;
  readonly $extensions?: Readonly<Record<string, unknown>>;
}

export interface DtcgShadowValue {
  readonly color: DtcgColorValue;
  readonly offsetX: DtcgDimensionValue;
  readonly offsetY: DtcgDimensionValue;
  readonly blur: DtcgDimensionValue;
  readonly spread: DtcgDimensionValue;
  readonly inset?: boolean;
}

export type ColorToken = DtcgToken<DtcgColorValue, 'color'>;
export type DimensionToken =
  | DtcgToken<DtcgDimensionValue, 'dimension'>
  | DtcgToken<number, 'number'>;

export interface WidgetPalette {
  readonly page: ColorToken;
  readonly surface: ColorToken;
  readonly surfaceSubtle: ColorToken;
  readonly surfaceHover: ColorToken;
  readonly text: ColorToken;
  readonly textSecondary: ColorToken;
  readonly textMuted: ColorToken;
  readonly border: ColorToken;
  readonly accent: ColorToken;
  readonly accentHover: ColorToken;
  readonly accentSubtle: ColorToken;
  readonly onAccent: ColorToken;
  readonly focus: ColorToken;
  readonly status: Readonly<
    Record<
      'success' | 'warning' | 'danger' | 'info',
      Readonly<{
        text: ColorToken;
        surface: ColorToken;
        border: ColorToken;
      }>
    >
  >;
  readonly skeleton: Readonly<{ base: ColorToken; highlight: ColorToken }>;
  readonly overlay: ColorToken;
  readonly overlayText: ColorToken;
  readonly overlayControl: ColorToken;
  readonly overlayControlHover: ColorToken;
  readonly overlayCaption: ColorToken;
}

function token<T, TType extends DtcgTokenType>(
  type: TType,
  value: T,
  description?: string,
  extensions?: Readonly<Record<string, unknown>>
): DtcgToken<T, TType> {
  return freezeGroup({
    $type: type,
    $value: value,
    ...(description === undefined ? {} : { $description: description }),
    ...(extensions === undefined ? {} : { $extensions: extensions }),
  });
}

function freezeGroup<T extends object>(value: T): Readonly<T> {
  for (const child of Object.values(value)) {
    if (child !== null && typeof child === 'object') {
      freezeGroup(child);
    }
  }
  return Object.freeze(value);
}

interface FmpCssExtensionValue {
  readonly value: string;
  readonly category?: 'dimension';
  readonly unit?: string;
}

function cssExtensions(
  value: string,
  metadata: Omit<FmpCssExtensionValue, 'value'> = {}
): Readonly<Record<typeof FMP_CSS_TOKEN_EXTENSION, FmpCssExtensionValue>> {
  return freezeGroup({
    [FMP_CSS_TOKEN_EXTENSION]: { value, ...metadata },
  });
}

function parseColorValue(cssValue: string): DtcgColorValue {
  const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i.exec(cssValue);
  if (hex) {
    const channels = [hex[1], hex[2], hex[3]].map((channel) => Number.parseInt(channel, 16));
    return {
      colorSpace: 'srgb',
      components: [channels[0] / 255, channels[1] / 255, channels[2] / 255],
      ...(hex[4] === undefined ? {} : { alpha: Number.parseInt(hex[4], 16) / 255 }),
      hex: `#${hex[1]}${hex[2]}${hex[3]}`.toLowerCase() as `#${string}`,
    };
  }

  const rgba = /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(0(?:\.\d+)?|1(?:\.0+)?)\s*\)$/i.exec(
    cssValue
  );
  if (!rgba) throw new Error(`Unsupported canonical color syntax: ${cssValue}`);
  const channels = [Number(rgba[1]), Number(rgba[2]), Number(rgba[3])];
  return {
    colorSpace: 'srgb',
    components: [channels[0] / 255, channels[1] / 255, channels[2] / 255],
    alpha: Number(rgba[4]),
    hex: `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}` as `#${string}`,
  };
}

function color(value: string, description?: string): ColorToken {
  return token(
    'color',
    parseColorValue(value),
    description,
    value.startsWith('rgba(') ? cssExtensions(value) : undefined
  );
}

function parseDimensionValue(value: string): { readonly value: number; readonly unit: string } {
  const match = /^(-?(?:\d+|\d*\.\d+))([a-z]+|%)$/i.exec(value);
  if (!match) throw new Error(`Unsupported canonical dimension syntax: ${value}`);
  return { value: Number(match[1]), unit: match[2] };
}

function dimension(value: string, description?: string): DimensionToken {
  const parsed = parseDimensionValue(value);
  if (parsed.unit === 'px' || parsed.unit === 'rem') {
    return token('dimension', { value: parsed.value, unit: parsed.unit }, description);
  }

  // DTCG 2025.10 dimensions only admit px/rem. Keep relative CSS values as a
  // valid number token, with the exact CSS dimension in a namespaced extension.
  return token(
    'number',
    parsed.value,
    description,
    cssExtensions(value, {
      category: 'dimension',
      unit: parsed.unit,
    })
  );
}

function duration(value: string, description?: string): DtcgToken<DtcgDurationValue, 'duration'> {
  const match = /^(-?(?:\d+|\d*\.\d+))(ms|s)$/.exec(value);
  if (!match) throw new Error(`Unsupported canonical duration syntax: ${value}`);
  return token('duration', { value: Number(match[1]), unit: match[2] as 'ms' | 's' }, description);
}

const number = (value: number, description?: string): DtcgToken<number, 'number'> =>
  token('number', value, description);

function extensionCssValue(tokenValue: DtcgToken<unknown, DtcgTokenType>): string | undefined {
  const extension = tokenValue.$extensions?.[FMP_CSS_TOKEN_EXTENSION];
  if (extension === null || typeof extension !== 'object') return undefined;
  const value = (extension as Partial<FmpCssExtensionValue>).value;
  return typeof value === 'string' ? value : undefined;
}

export function colorValueToCss(value: DtcgColorValue): string {
  if (value.hex && (value.alpha === undefined || value.alpha === 1)) return value.hex;
  const channels = value.components.map((component) => Math.round(component * 255));
  const alpha = value.alpha ?? 1;
  return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${alpha})`;
}

function dimensionValueToCss(value: DtcgDimensionValue | DtcgDurationValue): string {
  return `${value.value}${value.unit}`;
}

function shadowValueToCss(value: DtcgShadowValue): string {
  const inset = value.inset ? 'inset ' : '';
  return `${inset}${dimensionValueToCss(value.offsetX)} ${dimensionValueToCss(value.offsetY)} ${dimensionValueToCss(value.blur)} ${dimensionValueToCss(value.spread)} ${colorValueToCss(value.color)}`;
}

/** Serialize a canonical DTCG token into the widget's exact CSS spelling. */
export function tokenToCssValue(tokenValue: DtcgToken<unknown, DtcgTokenType>): string {
  const extendedCss = extensionCssValue(tokenValue);
  if (extendedCss !== undefined) return extendedCss;

  switch (tokenValue.$type) {
    case 'color':
      return colorValueToCss(tokenValue.$value as DtcgColorValue);
    case 'dimension':
      return dimensionValueToCss(tokenValue.$value as DtcgDimensionValue);
    case 'duration':
      return dimensionValueToCss(tokenValue.$value as DtcgDurationValue);
    case 'fontFamily': {
      const value = tokenValue.$value as string | readonly string[];
      return typeof value === 'string' ? value : value.join(', ');
    }
    case 'fontWeight':
    case 'number':
    case 'strokeStyle':
      return String(tokenValue.$value);
    case 'shadow': {
      const value = tokenValue.$value as DtcgShadowValue | readonly DtcgShadowValue[];
      return (Array.isArray(value) ? value : [value]).map(shadowValueToCss).join(', ');
    }
    case 'cubicBezier':
      return `cubic-bezier(${(tokenValue.$value as readonly number[]).join(', ')})`;
  }
}

function makePalette(values: {
  page: string;
  surface: string;
  surfaceSubtle: string;
  surfaceHover: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  accent: string;
  accentHover: string;
  accentSubtle: string;
  onAccent: string;
  focus: string;
  success: readonly [string, string, string];
  warning: readonly [string, string, string];
  danger: readonly [string, string, string];
  info: readonly [string, string, string];
  skeleton: readonly [string, string];
  overlay: string;
  overlayText: string;
  overlayControl: string;
  overlayControlHover: string;
  overlayCaption: string;
}): WidgetPalette {
  return freezeGroup({
    page: color(values.page, 'Host-adjacent page canvas'),
    surface: color(values.surface, 'Primary widget surface'),
    surfaceSubtle: color(values.surfaceSubtle, 'Subdued grouped surface'),
    surfaceHover: color(values.surfaceHover, 'Interactive hover surface'),
    text: color(values.text, 'Primary text'),
    textSecondary: color(values.textSecondary, 'Secondary text'),
    textMuted: color(values.textMuted, 'Muted text that still meets AA'),
    border: color(values.border, 'Load-bearing UI boundary'),
    accent: color(values.accent, 'Primary action and brand accent'),
    accentHover: color(values.accentHover, 'Primary action hover accent'),
    accentSubtle: color(values.accentSubtle, 'Selected/accent-tinted surface'),
    onAccent: color(values.onAccent, 'Text and icons on accent'),
    focus: color(values.focus, 'Keyboard focus indicator'),
    status: freezeGroup({
      success: freezeGroup({
        text: color(values.success[0]),
        surface: color(values.success[1]),
        border: color(values.success[2]),
      }),
      warning: freezeGroup({
        text: color(values.warning[0]),
        surface: color(values.warning[1]),
        border: color(values.warning[2]),
      }),
      danger: freezeGroup({
        text: color(values.danger[0]),
        surface: color(values.danger[1]),
        border: color(values.danger[2]),
      }),
      info: freezeGroup({
        text: color(values.info[0]),
        surface: color(values.info[1]),
        border: color(values.info[2]),
      }),
    }),
    skeleton: freezeGroup({
      base: color(values.skeleton[0]),
      highlight: color(values.skeleton[1]),
    }),
    overlay: color(values.overlay, 'Modal scrim'),
    overlayText: color(values.overlayText, 'Text and icons on media scrims'),
    overlayControl: color(values.overlayControl, 'Control on a media scrim'),
    overlayControlHover: color(values.overlayControlHover, 'Hovered control on a media scrim'),
    overlayCaption: color(values.overlayCaption, 'Caption backing on a media scrim'),
  });
}

/** Safe, self-owned light palette. */
export const lightPalette = makePalette({
  page: '#ffffff',
  surface: '#ffffff',
  surfaceSubtle: '#f5f7fa',
  surfaceHover: '#e8f2ff',
  text: '#1a1a1a',
  textSecondary: '#333333',
  textMuted: '#595959',
  border: '#767676',
  accent: '#0066cc',
  accentHover: '#0052a3',
  accentSubtle: '#e1efff',
  onAccent: '#ffffff',
  focus: '#0066cc',
  success: ['#1f6b2a', '#e8f5e9', '#3b7d43'],
  warning: ['#713707', '#fff2cf', '#8a520f'],
  danger: ['#8f1d24', '#fff0f1', '#a13a40'],
  info: ['#12517b', '#e8f4fd', '#39749b'],
  skeleton: ['#e3e7ed', '#f7f9fb'],
  overlay: 'rgba(0, 0, 0, 0.8)',
  overlayText: '#ffffff',
  overlayControl: 'rgba(0, 0, 0, 0.55)',
  overlayControlHover: 'rgba(0, 0, 0, 0.72)',
  overlayCaption: 'rgba(0, 0, 0, 0.68)',
});

/** Safe, self-owned dark palette. */
export const darkPalette = makePalette({
  page: '#0d1117',
  surface: '#161b22',
  surfaceSubtle: '#21262d',
  surfaceHover: '#223a55',
  text: '#f0f3f6',
  textSecondary: '#d0d7de',
  textMuted: '#b1bac4',
  border: '#8b949e',
  accent: '#58a6ff',
  accentHover: '#79c0ff',
  accentSubtle: '#1f3b5b',
  onAccent: '#0d1117',
  focus: '#58a6ff',
  success: ['#8ddb8c', '#173b24', '#71c878'],
  warning: ['#f2cc60', '#3f2d13', '#d8aa3a'],
  danger: ['#ffb4b8', '#4c1d24', '#f0757b'],
  info: ['#9bd3ff', '#183956', '#79c0ff'],
  skeleton: ['#30363d', '#484f58'],
  overlay: 'rgba(0, 0, 0, 0.88)',
  overlayText: '#ffffff',
  overlayControl: 'rgba(0, 0, 0, 0.65)',
  overlayControlHover: 'rgba(0, 0, 0, 0.82)',
  overlayCaption: 'rgba(0, 0, 0, 0.76)',
});

export type WidgetScheme = 'light' | 'dark';

const restingShadow = freezeGroup([
  freezeGroup({
    color: parseColorValue('#0000000f'),
    offsetX: { value: 0, unit: 'px' },
    offsetY: { value: 1, unit: 'px' },
    blur: { value: 2, unit: 'px' },
    spread: { value: 0, unit: 'px' },
  }),
  freezeGroup({
    color: parseColorValue('#0000001a'),
    offsetX: { value: 0, unit: 'px' },
    offsetY: { value: 1, unit: 'px' },
    blur: { value: 3, unit: 'px' },
    spread: { value: 0, unit: 'px' },
  }),
] satisfies DtcgShadowValue[]);
const raisedShadow = freezeGroup([
  freezeGroup({
    color: parseColorValue('#0000001a'),
    offsetX: { value: 0, unit: 'px' },
    offsetY: { value: 4, unit: 'px' },
    blur: { value: 12, unit: 'px' },
    spread: { value: 0, unit: 'px' },
  }),
] satisfies DtcgShadowValue[]);

/**
 * Named build-time query boundaries. CSS custom properties are intentionally
 * not used in query conditions. Viewport boundaries remain compatibility inputs
 * for pre-refresh views; Slice 2 views use the container boundaries for their
 * enhanced layouts and keep mobile-first rules as the no-CQ fallback.
 */
export const BREAKPOINTS = freezeGroup({
  container: freezeGroup({
    compact: '480px',
    wide: '960px',
  }),
  viewport: freezeGroup({
    smallMax: '479px',
    mediumMin: '480px',
    mediumMax: '768px',
  }),
});

export const tokens = freezeGroup({
  color: freezeGroup({ light: lightPalette, dark: darkPalette }),
  typography: freezeGroup({
    family: freezeGroup({
      inherited: token(
        'fontFamily',
        ['inherit'] as const,
        'Merchant font inherited at the carrier'
      ),
    }),
    size: freezeGroup({
      xs: dimension('12px'),
      sm: dimension('13px'),
      base: dimension('16px'),
      lg: dimension('20px'),
      xl: dimension('25px'),
      xxl: dimension('31px'),
    }),
    weight: freezeGroup({
      regular: token('fontWeight', 400),
      semibold: token('fontWeight', 600),
    }),
    lineHeight: freezeGroup({
      icon: number(1),
      tight: number(1.2),
      body: number(1.5),
      heading: number(1.25),
    }),
    letterSpacing: freezeGroup({
      label: dimension('0.01em'),
    }),
  }),
  dimension: freezeGroup({
    space: freezeGroup({
      xs: dimension('4px'),
      sm: dimension('8px'),
      md: dimension('12px'),
      lg: dimension('16px'),
      xl: dimension('20px'),
      xxl: dimension('24px'),
      xxxl: dimension('32px'),
      xxxxl: dimension('48px'),
    }),
    radius: freezeGroup({
      compact: dimension('4px'),
      control: dimension('8px'),
      surface: dimension('12px'),
      pill: dimension('9999px'),
    }),
    border: freezeGroup({
      hairline: dimension('1px'),
      focus: dimension('2px'),
    }),
    /** Exact-preserving migration values; not a second design scale. */
    geometry: freezeGroup({
      nudge: dimension('1px'),
      xxs: dimension('2px'),
      three: dimension('3px'),
      six: dimension('6px'),
      eight: dimension('8px'),
      ten: dimension('10px'),
      twelve: dimension('12px'),
      fourteen: dimension('14px'),
      fifteen: dimension('15px'),
      sixteen: dimension('16px'),
      eighteen: dimension('18px'),
      twenty: dimension('20px'),
      twentyFour: dimension('24px'),
      twentyFive: dimension('25px'),
      twentyEight: dimension('28px'),
      thirtyOne: dimension('31px'),
      thirtyTwo: dimension('32px'),
      thirtySix: dimension('36px'),
      forty: dimension('40px'),
      touch: dimension('44px'),
      fortyEight: dimension('48px'),
      fifty: dimension('50px'),
      sixty: dimension('60px'),
      sixtyFour: dimension('64px'),
      sixtyFive: dimension('65px'),
      seventyTwo: dimension('72px'),
      eighty: dimension('80px'),
      ninetySix: dimension('96px'),
      oneHundred: dimension('100px'),
      oneTen: dimension('110px'),
      oneTwenty: dimension('120px'),
      oneSixty: dimension('160px'),
      twoHundred: dimension('200px'),
      threeHundred: dimension('300px'),
      threeTwenty: dimension('320px'),
      fourHundred: dimension('400px'),
      fourEighty: dimension('480px'),
      sixHundred: dimension('600px'),
    }),
  }),
  elevation: freezeGroup({
    resting: token(
      'shadow',
      restingShadow,
      undefined,
      cssExtensions('0px 1px 2px 0px #0000000f, 0px 1px 3px 0px #0000001a')
    ),
    raised: token('shadow', raisedShadow, undefined, cssExtensions('0px 4px 12px 0px #0000001a')),
  }),
  layer: freezeGroup({
    base: number(0),
    raised: number(1),
    skipLink: number(1000),
    modal: number(10000),
  }),
  outline: freezeGroup({
    width: dimension('2px'),
    offset: dimension('2px'),
    insetOffset: dimension('-2px'),
    style: token('strokeStyle', 'solid'),
  }),
  motion: freezeGroup({
    duration: freezeGroup({
      instant: duration('0ms'),
      fast: duration('150ms'),
      normal: duration('200ms'),
      slow: duration('300ms'),
      spin: duration('800ms'),
      shimmer: duration('1500ms'),
    }),
    easing: freezeGroup({
      standard: token('cubicBezier', [0.2, 0, 0, 1] as const),
    }),
  }),
  layout: freezeGroup({
    widgetMax: dimension('800px', 'Base frame before explicit view or Hub overrides'),
    viewMax: dimension('1100px', 'Recognized Hub and refreshed Ask/Browse frame'),
    proseMax: dimension('70ch'),
    lightboxMaxInline: dimension(
      '90vw',
      'Bounded viewport-relative media size; preserves a visible dismissal gutter'
    ),
    lightboxMaxBlock: dimension(
      '85vh',
      'Bounded viewport-relative media size; preserves caption and dismissal space'
    ),
    skeleton: freezeGroup({
      titleInline: dimension('70%', 'Question-list title placeholder width'),
      titleBlock: dimension('22px', 'Question-list title placeholder height'),
      descriptionInline: dimension('95%', 'Browse description placeholder width'),
      descriptionBlock: dimension('16px', 'Browse description placeholder height'),
      badgeInline: dimension('80px', 'Status-badge placeholder width'),
      badgeBlock: dimension('24px', 'Status-badge placeholder height'),
      metaInline: dimension('90px', 'Metadata placeholder width'),
      metaShortInline: dimension('45px', 'Short metadata placeholder width'),
      metaBlock: dimension('16px', 'Metadata placeholder height'),
    }),
  }),
  breakpoint: freezeGroup({
    container: freezeGroup({
      compact: dimension(BREAKPOINTS.container.compact),
      wide: dimension(BREAKPOINTS.container.wide),
    }),
    viewport: freezeGroup({
      smallMax: dimension(BREAKPOINTS.viewport.smallMax),
      mediumMin: dimension(BREAKPOINTS.viewport.mediumMin),
      mediumMax: dimension(BREAKPOINTS.viewport.mediumMax),
    }),
  }),
});

export type WidgetTokens = typeof tokens;
